import { useState, useRef, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import apiAgent from "@/app/api/apiAgent";
import { withRouter } from "@/app/hooks/withRouter";
import { StoreContext } from "@/app/stores/store";
import { OMSTabBox } from "@/app/common/common-components/OMSTabBox";
import { OMSFavourite } from "@/app/common/common-components/OMSFavourite";
import { OMSButtonStyleL } from "@/app/common/common-components/OMSButton";
import { OMSPurchaseCredit } from "@/app/common/common-components/OMSPurchaseCredit";
import { OMSDropdown } from "@/app/common/common-components/OMSDropdown";
import ConfirmationModalPopup from "@/app/common/common-components/ModalPopups/ConfirmationModalPopup";
import PersonalTab from "@/features/profile/tabs/PersonalTab";
import ProfileTab from "@/features/profile/tabs/ProfileTab";
import IntegrationsTab from "@/features/profile/tabs/IntegrationsTab";
import PortalAccessTab from "@/features/profile/tabs/PortalAccessTab";
import MessageTemplatesTab from "@/features/profile/tabs/messageTemplates/MessageTemplatesTab";
import AccountTab from "@/features/profile/tabs/AccountTab";
import { useNavigate } from "react-router";

const UserBox = ({ data }) => {
  const navigate = useNavigate();
  const { userStore } = useContext(StoreContext);
  const purchaseCreditRef = useRef();
  const integrationRef = useRef();
  const isCreate = data.userID == "" || data.userID == null;

  const isSuperUser = userStore.userSession.role === "EntegralSuperUser";
  const messageTemplatesEnabled = isSuperUser && !!data.userID;

  const [state, setState] = useState({
    ActiveTabID: "account",
    UserID: data.userID,
    Title: data.title,
    FirstName: data.firstName,
    LastName: data.lastName,
    BirthDate: getFormattedBirthDate(data.birthDate),
    Gender: data.gender,
    EmailAddress: data.emailAddress,
    Password: data.password,
    Job: data.job,
    Status: data.status,
    StatusOld: data.status,
    Role: data.role,
    Photo: data.photo,
    PhotoUrl: data.photoUrl,
    PhotoUpload: null,
    Profile: data.profile,
    Credentials: data.credentials,
    UserIMAP: data.userIMAP,
    OfficeID: data.officeID,
    NationalIDNumber: data.nationalIDNumber,
    Contact: JSON.parse(data.contact),
    IsReceivesFlexInquiries: data.isReceivesFlexInquiries,
    IsReceivesListingExpiry: data.isReceivesListingExpiry,
    CanSendEmail: data.canSendEmail,
    CanSendSMS: data.canSendSMS,
    UserPortals: data.userPortals,
    TransferAgentID: "",
    Credit: data.credit,
    mdDeleteAgentAction: 1,
    mdIsDisabled: false,
    mdTotalListings: "",
    mdActiveListings: "",
    mdSyncActiveListings: "",
    mdListingCount: false,
    mdTransferAgentID: "",
    progressbar_class: "progress-bar-danger",
    progressbar_width: "0%",
    progressbar_text: "Enter password",
    password_isstrong: false,
    ButtonStatus: {
      disabled: false,
      processing_save: false,
      processing_delete: false,
    },
    manageListingsDD: data.manageListingsDD,
    manageListingsValue: data.manageListings,
    restrictedPortals: data.restrictedPortals,
    ConfirmationModalProps: {
      showModal: false,
      isConfirmed: (result) => {},
      title: "",
      body: "",
    },
    TwoFASettings: JSON.parse(data.twoFASettingsJson),
    isPublishToPortals: data.isPublishToPortals,
  });

  const [tabContent, setTabContent] = useState(() => {
    const content = [
      { link: "#account", title: "Account" },
      { link: "#personal", title: "Personal" },
      { link: "#profile", title: "Profile" },
    ];

    if (data.userID) {
      content.push({ link: "#email", title: "Integrations" });
      if (messageTemplatesEnabled) {
        content.push({ link: "#messagetemplates", title: "Message Templates" });
      }
    }

    if (data.actions.indexOf("select_portal") != -1 && data.userID) {
      content.push({ link: "#portalaccess", title: "Portal & User Access" });
    }

    return content;
  });

  // Add useEffect to check password strength on mount
  useEffect(() => {
    if (state.Password) {
      CheckPasswordStrength({ target: { value: state.Password } });
    }
  }, []);

  function getFormattedBirthDate(date) {
    const format = "DD-MM-yyyy";
    if (date) {
      let isoDateString = moment(date, format).toISOString();
      return new Date(isoDateString);
    }
    return null;
  }

  // Handler functions
  const handleChange = (e) => {
    setState((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleStatusChanged = () => {
    setState((prev) => ({
      ...prev,
      Status: prev.Status === 1 ? 2 : 1,
    }));
  };

  const handleGenderChange = (e) => {
    setState((prev) => ({
      ...prev,
      Gender: e.target.id === "rbtnMale" ? 2 : 1,
    }));
  };

  const handleOfficeIDChange = (e) => {
    setState((prev) => ({
      ...prev,
      OfficeID: e.newValue,
    }));
  };

  const handleRoleChange = (e) => {
    setState((prev) => ({
      ...prev,
      Role: e.newValue,
    }));
  };

  const handleTwoFactorChange = (name, value) => {
    setState((prev) => ({
      ...prev,
      TwoFASettings: {
        ...prev.TwoFASettings,
        [name]: value,
      },
    }));
  };

  const handleContactChange = (name, value) => {
    setState((prev) => ({
      ...prev,
      Contact: {
        ...prev.Contact,
        [name]: value,
      },
    }));
  };

  const handleDialingCodeChange = (e) => {
    handleContactChange("phone_dialing_code", e.newValue);
  };

  const handlePhoneChange = (name, value) => {
    handleContactChange("phone", value);
  };

  const handleResidentialAddressChange = (e) => {
    handleContactChange(e.target.name, e.target.value);
  };

  const OnCropSubmit = (d) => {
    setState((prev) => ({
      ...prev,
      PhotoUpload: d,
    }));
  };

  const CheckPasswordStrength = (e) => {
    const password = e.target.value;
    let passed = -1;
    if (password) {
      const regex = [
        "[A-Z]", //Uppercase Alphabet.
        "[a-z]", //Lowercase Alphabet.
        "[0-9]", //Digit.
        "[$@$!%*#?&]", //Special Character.
      ];

      passed = 0;
      //Validate for each Regular Expression.
      for (let i = 0; i < regex.length; i++) {
        if (new RegExp(regex[i]).test(password)) {
          passed++;
        }
      }

      //Validate for length of Password.
      if (passed > 2 && password.length > 6) {
        passed++;
      }

      if (password.length < 6) {
        if (passed > 2) passed--;
        else passed = 0;
      }
    }

    let progressbar_class = "";
    let progressbar_width = "";
    let progressbar_text = "";
    let password_isstrong = false;

    switch (passed) {
      case -1:
        progressbar_class = "progress-bar-danger";
        progressbar_width = "0%";
        progressbar_text = "";
        break;
      case 0:
      case 1:
        progressbar_class = "progress-bar-danger";
        progressbar_width = "25%";
        progressbar_text = "Weak";
        break;
      case 2:
        progressbar_class = "progress-bar-warning";
        progressbar_width = "50%";
        progressbar_text = "Medium";
        break;
      case 3:
      case 4:
        progressbar_class = "progress-bar-info";
        progressbar_width = "75%";
        progressbar_text = "Strong";
        password_isstrong = true;
        break;
      case 5:
        progressbar_class = "progress-bar-success";
        progressbar_width = "100%";
        progressbar_text = "Very Strong";
        password_isstrong = true;
        break;
    }

    setState((prev) => ({
      ...prev,
      password_isstrong,
      progressbar_class,
      progressbar_width,
      progressbar_text,
    }));
  };

  const handleCheckboxChangedP = (e) => {
    const name = e.target.name;
    setState((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const handlePublishToPortalChanged = () => {
    setState((prev) => ({
      ...prev,
      isPublishToPortals: !prev.isPublishToPortals,
    }));
  };

  const handleCheckboxChangedPortalAccess = async (event, idx) => {
    let updatedPortals = [...state.UserPortals];
    let updatedRestrictedPortals = [...state.restrictedPortals];
    let element = {
      portalID: updatedPortals[idx].portalID,
      portalName: updatedPortals[idx].portalName,
      isEnabled: event.target.checked,
    };

    updatedPortals.splice(idx, 1);
    updatedPortals.splice(idx, 0, element);

    if (updatedPortals[idx].isEnabled) {
      if (updatedRestrictedPortals.indexOf(updatedPortals[idx].portalID) >= 0) {
        updatedRestrictedPortals.splice(updatedRestrictedPortals.indexOf(updatedPortals[idx].portalID), 1);
      }
    } else if (!updatedPortals[idx].isEnabled) {
      if (updatedRestrictedPortals.indexOf(updatedPortals[idx].portalID) < 0) {
        updatedRestrictedPortals.push(updatedPortals[idx].portalID);
      }
    }

    setState((prev) => ({
      ...prev,
      UserPortals: updatedPortals,
      restrictedPortals: updatedRestrictedPortals,
    }));
  };

  const handleRoleDD = (selected) => {
    setState((prev) => ({
      ...prev,
      manageListingsValue: selected,
    }));
  };

  const setActiveTabID = (tabID) => {
    setState((prev) => ({
      ...prev,
      ActiveTabID: tabID,
    }));
  };

  // Action handlers
  const onCancelClick = () => {
    navigate("/Settings/Users");
  };

  const onHistoryClick = () => {
    const location = `/settings/auditTrail?ModuleID=1&ObjectID=${data.userId}`;
    navigate(location);
  };

  const onLogInAsClick = () => {
    userStore.loginAs(state.UserID).catch((error) => toast.error(error.response.data));
  };

  const onPurchaseClick = () => {
    purchaseCreditRef.current.onOpen(state.UserID);
  };

  // Delete-related handlers
  const onDeleteClick = () => {
    setState((prev) => ({
      ...prev,
      mdDeleteAgentAction: 1,
      mdIsDisabled: false,
      mdTotalListings: "",
      mdActiveListings: "",
      mdSyncActiveListings: "",
      mdListingCount: false,
      mdTransferAgentID: "",
    }));
    window.$("#deleteUserModal").modal("show");
    loadUsersListingCount();
  };

  const onInactiveSyncDeleteClick = () => {
    setState((prev) => ({
      ...prev,
      mdDeleteAgentAction: 1,
      mdIsDisabled: true,
      mdTotalListings: "",
      mdActiveListings: "",
      mdSyncActiveListings: "",
      mdListingCount: false,
      mdTransferAgentID: "",
    }));
    window.$("#deleteUserModal").modal("show");
    loadUsersListingCount();
  };

  const handleDeleteAgentActionChange = (e) => {
    const mdDeleteAgentAction = e.target.id === "rbtnDeleteListings" ? 2 : 1;
    setState((prev) => ({
      ...prev,
      mdDeleteAgentAction,
    }));
  };

  const onTransferListingClick = () => {
    setState((prev) => ({
      ...prev,
      TransferAgentID: "",
    }));
    window.$("#transferModal").modal("show");
  };

  const handleDDChange = (e) => {
    setState((prev) => ({
      ...prev,
      [e.id]: e.newValue,
    }));
  };

  const handleProcessClick = () => {
    const AgentID = state.TransferAgentID;
    if (AgentID !== "") {
      const title = "Transfer agent?";
      const body = "Do you want to transfer listings now? This cannot be undone.";
      const callback = executeProcessClick;
      showPopupAndDeleteOnConfirmation(title, body, callback);
    }
  };

  const executeProcessClick = () => {
    setState((prev) => ({
      ...prev,
      ButtonStatus: {
        ...prev.ButtonStatus,
        disabled: true,
        processing_save: false,
        processing_delete: true,
      },
    }));

    apiAgent.Setting.transferUserAjax(state.UserID, state.TransferAgentID)
      .then((response) => {
        toast.success(response);
      })
      .catch((error) => toast.error(error.response.data))
      .finally(() => {
        setState((prev) => ({
          ...prev,
          ButtonStatus: {
            ...prev.ButtonStatus,
            disabled: false,
            processing_save: false,
            processing_delete: false,
          },
        }));
      });
  };

  const handleDeleteAgentProcessClick = () => {
    if (state.mdDeleteAgentAction === 1 && state.mdTransferAgentID === "") {
      toast.info("Please select agent to transfer listings");
      return;
    }

    const title = "Delete/Disable agent?";
    const body = "Do you want to delete/disable agent now? This cannot be undone.";
    const callback = executeDeleteAgentProcessClick;
    showPopupAndDeleteOnConfirmation(title, body, callback);
  };

  const executeDeleteAgentProcessClick = () => {
    window.$("#deleteUserModal").modal("hide");
    setState((prev) => ({
      ...prev,
      ButtonStatus: {
        ...prev.ButtonStatus,
        disabled: true,
        processing_save: false,
        processing_delete: true,
      },
    }));

    apiAgent.Setting.deleteUserAjax(state.UserID, state.mdIsDisabled, state.mdDeleteAgentAction, state.mdTransferAgentID)
      .then((response) => {
        toast.success(response);
        navigate("/Settings/Users");
      })
      .catch((error) => toast.error(error.response.data))
      .finally(() => {
        setState((prev) => ({
          ...prev,
          ButtonStatus: {
            ...prev.ButtonStatus,
            disabled: false,
            processing_save: false,
            processing_delete: false,
          },
        }));
      });
  };

  // Form submission
  const doValidate = () => {
    const RegEmail = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,10})+$/;
    let failed = false;

    if (!state.FirstName.trim()) {
      toast.info("Please enter first name");
      failed = true;
    }

    if (!state.LastName.trim()) {
      toast.info("Please enter last name");
      failed = true;
    }

    if (!state.EmailAddress.trim() || !RegEmail.test(state.EmailAddress)) {
      toast.info("Please enter valid email address");
      failed = true;
    }

    if (!state.Password.trim()) {
      toast.info("Please enter password");
      failed = true;
    }

    if (!state.password_isstrong) {
      toast.info("Please increase password strength. Use a combination of lowercase, uppercase, numerical and special characters");
      failed = true;
    }

    return !failed;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!doValidate()) return;

    if (state.UserID !== "" && state.Status === 2 && state.StatusOld !== 2) {
      setState((prev) => ({
        ...prev,
        mdDeleteAgentAction: 1,
        mdIsDisabled: true,
        mdTotalListings: "",
        mdActiveListings: "",
        mdSyncActiveListings: "",
        mdListingCount: false,
        mdTransferAgentID: "",
      }));
      window.$("#deleteUserModal").modal("show");
      loadUsersListingCount();
      return;
    }

    const payload = {
      userID: state.UserID || "",
      title: state.Title || "",
      firstName: state.FirstName || "",
      lastName: state.LastName || "",
      birthDate: state.BirthDate ? state.BirthDate.toISOString() : "",
      gender: state.Gender || "",
      emailAddress: state.EmailAddress || "",
      password: state.Password || "",
      job: state.Job || "",
      status: state.Status || 1,
      role: state.Role || "",
      photo: state.Photo || "",
      photoUrl: state.PhotoUrl || "",
      photoUpload: state.PhotoUpload || null,
      profile: state.Profile || "",
      credentials: state.Credentials || "",
      officeID: state.OfficeID || "",
      nationalIDNumber: state.NationalIDNumber || "",
      strIsReceivesFlexInquiries: state.IsReceivesFlexInquiries ? "true" : "false",
      strIsReceivesListingExpiry: state.IsReceivesListingExpiry ? "true" : "false",
      strCanSendSMS: state.CanSendSMS ? "true" : "false",
      strCanSendEmail: state.CanSendEmail ? "true" : "false",
      manageListings: state.manageListingsValue || "",
      restrictedPortals: state.restrictedPortals || [],
      contact: JSON.stringify(state.Contact || {}),
      twoFASettingsJSON: JSON.stringify(state.TwoFASettings || {}),
      isPublishToPortals: state.isPublishToPortals ? "true" : "false",
    };

    if (integrationRef.current) {
      const userImapdata = integrationRef.current.getStateData();
      if (userImapdata) {
        payload.userIMAPJson = JSON.stringify(userImapdata);
      }
    }

    setState((prev) => ({
      ...prev,
      ButtonStatus: {
        ...prev.ButtonStatus,
        disabled: true,
        processing_save: true,
      },
    }));

    try {
      const response = await apiAgent.Setting.saveUserAjax(payload);
      toast.success(response);
      navigate("/Settings/Users");
    } catch (error) {
      toast.error(error.response.data);
    } finally {
      setState((prev) => ({
        ...prev,
        ButtonStatus: {
          ...prev.ButtonStatus,
          disabled: false,
          processing_save: false,
        },
      }));
    }
  };

  // Modal handlers
  const openCloseDeleteModal = (isOpen) => {
    setState((prev) => ({
      ...prev,
      ConfirmationModalProps: {
        ...prev.ConfirmationModalProps,
        showModal: isOpen,
      },
    }));
  };

  const showPopupAndDeleteOnConfirmation = (title, body, callback) => {
    setState((prev) => ({
      ...prev,
      ConfirmationModalProps: {
        title,
        body,
        showModal: true,
        isConfirmed: (isConfirmed) => {
          if (isConfirmed) {
            callback();
          }
          openCloseDeleteModal(false);
        },
      },
    }));
  };

  const loadUsersListingCount = async () => {
    try {
      const response = await apiAgent.Setting.getUsersListingsCount(state.UserID);
      setState((prev) => ({
        ...prev,
        mdTotalListings: response.all,
        mdActiveListings: response.active,
        mdSyncActiveListings: response.syncActive,
        mdListingCount: true,
      }));
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div>
      <ConfirmationModalPopup {...state.ConfirmationModalProps} />
      <form className="SaveUserForm" onSubmit={handleSubmit}>
        <div className="col-sm-12">
          <div className="panel panel-default oms-box">
            <div className="panel-heading">
              <div className="row">
                <div className="col-md-12">
                  <OMSFavourite favourite={data.favourite} /> &nbsp;
                  <label>{isCreate ? "CREATE" : state.Title + ". " + state.FirstName + " " + state.LastName}</label>
                </div>
              </div>
            </div>
            <div className="panel-body">
              <div className="col-md-12">
                <OMSTabBox id="userTabs" value={tabContent} setActiveTabId={setActiveTabID} />
                <div className="tab-content">
                  <div id="account" className={state.ActiveTabID === "account" ? "tab-pane fade in active" : "tab-pane fade"}>
                    <AccountTab
                      data={data}
                      state={state}
                      handleChange={handleChange}
                      handleStatusChanged={handleStatusChanged}
                      handleRoleChange={handleRoleChange}
                      handleOfficeIDChange={handleOfficeIDChange}
                      handleTwoFactorChange={handleTwoFactorChange}
                      OnCropSubmit={OnCropSubmit}
                      CheckPasswordStrength={CheckPasswordStrength}
                    />
                  </div>
                  <div id="personal" className={state.ActiveTabID === "personal" ? "tab-pane fade in active" : "tab-pane fade"}>
                    <PersonalTab
                      state={state}
                      handleChange={handleChange}
                      handleGenderChange={handleGenderChange}
                      handleResidentialAddressChange={handleResidentialAddressChange}
                    />
                  </div>
                  <div id="profile" className={state.ActiveTabID === "profile" ? "tab-pane fade in active" : "tab-pane fade"}>
                    <ProfileTab
                      state={state}
                      data={data}
                      handleChange={handleChange}
                      handleDialingCodeChange={handleDialingCodeChange}
                      handlePhoneChange={handlePhoneChange}
                      handleContactChange={handleContactChange}
                      handlePublishToPortalChanged={handlePublishToPortalChanged}
                    />
                  </div>
                  <div id="email" className={state.ActiveTabID === "email" ? "tab-pane fade in active" : "tab-pane fade"}>
                    <IntegrationsTab
                      state={state}
                      data={data}
                      handleCheckboxChangedP={handleCheckboxChangedP}
                      onPurchaseClick={onPurchaseClick}
                      integrationRef={integrationRef}
                    />
                  </div>
                  {messageTemplatesEnabled && (
                    <div id="messagetemplates" className={state.ActiveTabID === "messagetemplates" ? "tab-pane fade in active" : "tab-pane fade"}>
                      <MessageTemplatesTab userRole={state.Role} userId={state.UserID} officeId={state.OfficeID} />
                    </div>
                  )}
                  <div id="portalaccess" className={state.ActiveTabID === "portalaccess" ? "tab-pane fade in active" : "tab-pane fade"}>
                    <PortalAccessTab
                      state={state}
                      data={data}
                      handleCheckboxChangedPortalAccess={handleCheckboxChangedPortalAccess}
                      handleRoleDD={handleRoleDD}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="panel-footer">
              <div className="row">
                <div className="col-md-12">
                  {!isCreate && (
                    <div className="pull-left">
                      <div className="dropup">
                        <button className="btn btn-outline dropdown-toggle" type="button" data-toggle="dropdown">
                          More..
                          <span className="caret" />
                        </button>
                        <ul className="dropdown-menu">
                          {data.actions.indexOf("loginas") >= 0 && (
                            <li>
                              <OMSButtonStyleL
                                style={{ marginRight: 10 }}
                                type="button"
                                ui_icon="fa-user-o"
                                ui_type="cancel"
                                ui_text="Log in as"
                                ui_processing_text=""
                                processing={false}
                                disabled={state.ButtonStatus.disabled}
                                onClick={onLogInAsClick}
                              />
                            </li>
                          )}
                          {data.actions.indexOf("history") >= 0 && (
                            <li>
                              <OMSButtonStyleL
                                style={{ marginRight: 10 }}
                                type="button"
                                ui_icon="fa-history"
                                ui_type="cancel"
                                ui_text="History"
                                ui_processing_text=""
                                processing={false}
                                disabled={state.ButtonStatus.disabled}
                                onClick={onHistoryClick}
                              />
                            </li>
                          )}
                          {data.actions.indexOf("delete") >= 0 && (
                            <li>
                              <OMSButtonStyleL
                                style={{ marginRight: 10 }}
                                type="button"
                                ui_icon="fa-trash-o"
                                ui_type="cancel"
                                ui_text="Delete user"
                                ui_processing_text="Deleting.."
                                processing={state.ButtonStatus.processing_delete}
                                disabled={state.ButtonStatus.disabled}
                                onClick={onDeleteClick}
                              />
                            </li>
                          )}
                          {data.actions.indexOf("loginas") >= 0 && state.StatusOld === 2 && (
                            <li>
                              <OMSButtonStyleL
                                style={{ marginRight: 10 }}
                                type="button"
                                ui_icon="fa-trash-o"
                                ui_type="cancel"
                                ui_text="Inactive user(Sync listings)"
                                ui_processing_text="Inactive user.."
                                processing={state.ButtonStatus.processing_delete}
                                disabled={state.ButtonStatus.disabled}
                                onClick={onInactiveSyncDeleteClick}
                              />
                            </li>
                          )}
                          {data.actions.indexOf("transfer") >= 0 && (
                            <li>
                              <OMSButtonStyleL
                                style={{ marginRight: 10 }}
                                type="button"
                                ui_icon="fa-refresh"
                                ui_type="cancel"
                                ui_text="Transfer listings"
                                ui_processing_text=""
                                processing={false}
                                disabled={state.ButtonStatus.disabled}
                                onClick={onTransferListingClick}
                              />
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  )}
                  <div className="pull-right">
                    <OMSButtonStyleL
                      style={{ marginRight: 10 }}
                      type="button"
                      ui_icon="fa-times"
                      ui_type="cancel"
                      ui_text="Cancel"
                      ui_processing_text=""
                      processing={false}
                      disabled={state.ButtonStatus.disabled}
                      onClick={onCancelClick}
                    />
                    {data.actions.indexOf("save") >= 0 && (
                      <OMSButtonStyleL
                        type="submit"
                        ui_icon="fa-check"
                        ui_type="save"
                        ui_text="Save"
                        ui_processing_text="Saving.."
                        processing={state.ButtonStatus.processing_save}
                        disabled={state.ButtonStatus.disabled}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
      <OMSPurchaseCredit ref={purchaseCreditRef} ID="SaveUser" />

      {/* Transfer Modal */}
      <div className="modal fade" id="transferModal" tabIndex={-1} role="dialog" aria-labelledby="transferModalLabel">
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">×</span>
              </button>
              <h4 className="modal-title text-center" id="transferModalLabel">
                Transfer Listings
              </h4>
            </div>
            <div className="modal-body">
              <div className="row">
                <div className="form-group col-md-12">
                  <span>Note: Transfer listing to another agent, It can't be redone</span>
                </div>
                <div className="form-group col-md-12">
                  <label htmlFor="TransferAgentID">Transfer Listing To:</label>
                  <OMSDropdown
                    id="TransferAgentID"
                    options={data.transferAgentList}
                    valueField="value"
                    labelField="text"
                    value={state.TransferAgentID}
                    onChange={handleDDChange}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <div className="col-md-12">
                <button type="button" className="btn btn-default" data-dismiss="modal">
                  Close
                </button>
                <button type="button" onClick={handleProcessClick} className="btn btn-primary">
                  Process
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete User Modal */}
      <div className="modal fade" id="deleteUserModal" tabIndex={-1} role="dialog" aria-labelledby="deleteUserModal">
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">×</span>
              </button>
              <h4 className="modal-title text-center" id="deleteUserModal">
                Delete or Disable Agent
              </h4>
            </div>
            <div className="modal-body">
              <div className="row">
                <div className="form-group col-md-12">
                  <span>
                    Agent to delete/disable:- <b>{state.FirstName + " " + state.LastName}</b>
                  </span>
                  <span
                    style={{
                      display: "block",
                      marginTop: 8,
                      marginBottom: 2,
                    }}
                  >
                    Total listings:- {state.mdListingCount === false ? <i className="fa fa-spinner fa-spin"></i> : <b>{state.mdTotalListings}</b>}
                  </span>
                  <span
                    style={{
                      display: "block",
                      marginTop: 8,
                      marginBottom: 2,
                    }}
                  >
                    Active listings:- {state.mdListingCount === false ? <i className="fa fa-spinner fa-spin"></i> : <b>{state.mdActiveListings}</b>}
                  </span>
                  <span
                    style={{
                      display: "block",
                      marginTop: 8,
                      marginBottom: 5,
                    }}
                  >
                    Active listings(Sync):- {state.mdListingCount === false ? <i className="fa fa-spinner fa-spin"></i> : <b>{state.mdSyncActiveListings}</b>}
                  </span>
                  <span>Above listings for agent will be removed/transfered</span>
                </div>
                <div className="form-group col-md-12">
                  <label htmlFor="Action">Action</label>
                  <div className="form-inline" style={{ marginBottom: 10 }}>
                    <label className="radio-inline">
                      <input
                        type="radio"
                        id="rbtnTransferListings"
                        name="DeleteAgentAction"
                        checked={state.mdDeleteAgentAction === 1}
                        onChange={handleDeleteAgentActionChange}
                      />
                      Transfer listings (Recommended)
                    </label>
                    <label className="radio-inline">
                      <input
                        type="radio"
                        id="rbtnDeleteListings"
                        name="DeleteAgentAction"
                        checked={state.mdDeleteAgentAction === 2}
                        onChange={handleDeleteAgentActionChange}
                      />
                      Remove/Delete listings
                    </label>
                  </div>
                  {state.mdDeleteAgentAction !== 2 && (
                    <div className="form-group col-md-12">
                      <label htmlFor="mdTransferAgentID">Transfer Listings To:</label>
                      <OMSDropdown
                        id="mdTransferAgentID"
                        options={data.transferAgentList}
                        valueField="value"
                        labelField="text"
                        value={state.mdTransferAgentID}
                        onChange={handleDDChange}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <div className="col-md-12">
                  <button type="button" className="btn btn-default" data-dismiss="modal">
                    Close
                  </button>
                  <button type="button" onClick={handleDeleteAgentProcessClick} className="btn btn-primary">
                    Process
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withRouter(UserBox);
